export const KAIROS_WORKLIST_CACHE_READINESS = 'KAIROS_WORKLIST_CACHE_READINESS';

// Canonical backend contract should prefer renderCacheStatus / warmupStatus plus warmup counters.
// Snake_case fallbacks keep the column safe while roadmap-pt1 backend projection settles.
// Priority order: explicit readiness/status strings > boolean ready flags > warmup counters
// > storage tier hints > UNKNOWN. PARTIAL means most/some intended warmup targets
// are cached but coverage is incomplete or degraded, so it must not be displayed as COLD.
// The return shape is { state, label, detail, color }
// for the non-polling worklist chip contract used by roadmap-pt1 and future backend projection.

const READINESS_SORT_ORDER = { HOT: 0, PARTIAL: 1, WARMING: 2, COLD: 3, UNKNOWN: 4 };

const norm = value => String(value ?? '').trim().toLowerCase();

const truthy = value => value === true || ['true', 'yes', '1', 'ready', 'complete', 'completed'].includes(norm(value));

const numberOrNull = value => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

export const formatStudyReadiness = (study = {}) => {
  const explicit = norm(
    study.renderCacheStatus
      ?? study.render_cache_status
      ?? study.cacheReadiness
      ?? study.cache_readiness
      ?? study.warmupStatus
      ?? study.warmup_status
      ?? study.readiness
  );

  const pending = numberOrNull(study.warmupPending ?? study.warmup_pending ?? study.renderWarmupPending ?? study.render_warmup_pending);
  const running = numberOrNull(study.warmupRunning ?? study.warmup_running ?? study.renderWarmupRunning ?? study.render_warmup_running);
  const ready = numberOrNull(study.warmupReady ?? study.warmup_ready ?? study.renderWarmupReady ?? study.render_warmup_ready);
  const skipped = numberOrNull(study.warmupSkipped ?? study.warmup_skipped ?? study.renderWarmupSkipped ?? study.render_warmup_skipped);
  const total = numberOrNull(study.warmupTotal ?? study.warmup_total ?? study.renderWarmupTotal ?? study.render_warmup_total);
  const failed = numberOrNull(study.warmupFailed ?? study.warmup_failed ?? study.renderWarmupFailed ?? study.render_warmup_failed);

  if ((pending ?? 0) > 0 || (running ?? 0) > 0) {
    return { state: 'WARMING', label: 'WARMING', detail: 'study warmup in progress', color: 'warning' };
  }

  const exactHotCoverage = total !== null && total > 0
    && pending !== null && pending === 0
    && running !== null && running === 0
    && ready !== null && ready === total
    && skipped !== null && skipped === 0
    && failed !== null && failed === 0;

  if (['hot', 'ready', 'cached', 'warm', 'complete', 'completed'].includes(explicit)) {
    if (total !== null && total > 0) {
      if (exactHotCoverage) {
        return { state: 'HOT', label: 'HOT', detail: 'render cache ready', color: 'success' };
      }
      if ((ready ?? 0) > 0) {
        return { state: 'PARTIAL', label: 'PARTIAL', detail: 'partial cache coverage', color: 'warning' };
      }
      return { state: 'COLD', label: 'COLD', detail: 'no cached renderable frames', color: 'default' };
    }
    return { state: 'UNKNOWN', label: 'UNKNOWN', detail: 'cache readiness not reported', color: 'default' };
  }
  if (['partial', 'degraded', 'incomplete'].includes(explicit)) {
    return { state: 'PARTIAL', label: 'PARTIAL', detail: 'partial cache coverage', color: 'warning' };
  }
  if (['warming', 'queued', 'running', 'pending', 'prewarming', 'in_progress'].includes(explicit)) {
    return { state: 'WARMING', label: 'WARMING', detail: 'study warmup in progress', color: 'warning' };
  }
  if (['cold', 'miss', 'not_cached', 'not-ready', 'not_ready', 'none', 'failed', 'error', 'errored'].includes(explicit)) {
    return { state: 'COLD', label: 'COLD', detail: 'renders on demand', color: 'default' };
  }

  if (truthy(study.renderCacheReady ?? study.render_cache_ready ?? study.cacheReady ?? study.cache_ready ?? study.hotCacheReady ?? study.hot_cache_ready) && exactHotCoverage) {
    return { state: 'HOT', label: 'HOT', detail: 'render cache ready', color: 'success' };
  }

  if (exactHotCoverage) {
    return { state: 'HOT', label: 'HOT', detail: 'render cache ready', color: 'success' };
  }
  if (total !== null && total > 0 && ready !== null && ready > 0 && ready !== total) {
    return { state: 'PARTIAL', label: 'PARTIAL', detail: 'partial cache coverage', color: 'warning' };
  }
  if ((failed ?? 0) > 0) {
    return { state: 'COLD', label: 'COLD', detail: 'renders on demand', color: 'default' };
  }
  if ((skipped ?? 0) > 0 && (ready ?? 0) === 0) {
    return { state: 'COLD', label: 'COLD', detail: 'no cached renderable frames', color: 'default' };
  }
  if (total !== null && total > 0 && ready !== null && ready < total) {
    return { state: 'WARMING', label: 'WARMING', detail: 'study warmup in progress', color: 'warning' };
  }

  const storageTier = norm(study.storageTier ?? study.storage_tier);
  if (['cold', 'archive', 'nearline'].includes(storageTier)) {
    return { state: 'COLD', label: 'COLD', detail: 'renders on demand', color: 'default' };
  }

  return { state: 'UNKNOWN', label: 'UNKNOWN', detail: 'cache readiness not reported', color: 'default' };
};

export const readinessSortValue = readiness => READINESS_SORT_ORDER[readiness?.state || readiness] ?? READINESS_SORT_ORDER.UNKNOWN;
